import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import type { UIMessage } from "ai";
import type { SupportAssignment, LiveVisitor, SupportSnapshot } from "../../shared/support";
import { decodeHtmlEntities } from "../../shared/text";
import { formatTakeoverDelay, resolveTakeoverDelay } from "../../shared/support-settings";
import type { AdminUser } from "../../shared/admin-auth";
import { customerFacingName } from "./operator-name";
import { ProfilePhoto } from "./ProfilePhoto";

interface InboxSite { id: string; name: string; status: string; timeZone: string; aiTakeoverDelaySeconds: number }
type Api = <T>(path: string, init?: RequestInit) => Promise<T>;
interface Transcript { messages: UIMessage[]; support: SupportAssignment | null }
interface ChatAlert { type: "chat"; eventId: string; conversationId: string; newChat: boolean; preview: string }
type HubEvent = SupportSnapshot | ChatAlert | { type: "conversation"; conversationId: string };

export function LiveInbox({ user, sites, selectedSiteId, onSelectSite, api }: {
  user: AdminUser; sites: InboxSite[]; selectedSiteId: string; onSelectSite: (id: string) => void; api: Api;
}) {
  const operatorId = user.operatorId;
  const nameKey = `woo-chat-operator-name:${user.email}`;
  const [name, setName] = useState(() => customerFacingName(user, sessionStorage.getItem(nameKey)));
  const [nameDraft, setNameDraft] = useState(name);
  const [snapshots, setSnapshots] = useState<Record<string, SupportSnapshot>>({});
  const [connections, setConnections] = useState<Record<string, string>>({});
  const [alerts, setAlerts] = useState<Array<ChatAlert & { siteId: string; siteName: string }>>([]);
  const [selected, setSelected] = useState<{ siteId: string; conversationId: string } | null>(null);
  const [revision, setRevision] = useState(0);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const seen = useRef(new Set<string>());
  const audio = useRef<AudioContext | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const selectedRef = useRef(selected);
  const pendingMessage = useRef<{ id: string; text: string; conversationId: string } | null>(null);
  selectedRef.current = selected;

  const receive = useCallback((siteId: string, siteName: string, event: HubEvent) => {
    if (event.type === "snapshot") setSnapshots((current) => ({ ...current, [siteId]: event }));
    if (event.type === "conversation" && selectedRef.current?.siteId === siteId && selectedRef.current.conversationId === event.conversationId) setRevision((value) => value + 1);
    if (event.type !== "chat") return;
    const key = `${siteId}:${event.eventId}`;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    if (seen.current.size > 1_000) seen.current.delete(seen.current.values().next().value!);
    setAlerts((current) => [{ ...event, siteId, siteName }, ...current.filter((item) => item.conversationId !== event.conversationId || item.siteId !== siteId)].slice(0, 10));
    if (alertsEnabled) {
      if (audio.current?.state === "running") {
        const oscillator = audio.current.createOscillator();
        const gain = audio.current.createGain();
        oscillator.connect(gain); gain.connect(audio.current.destination);
        oscillator.frequency.value = 660; gain.gain.value = 0.08;
        gain.gain.exponentialRampToValueAtTime(0.001, audio.current.currentTime + 0.3);
        oscillator.start(); oscillator.stop(audio.current.currentTime + 0.3);
      }
      if ("Notification" in window && Notification.permission === "granted" && document.visibilityState !== "visible") {
        const notification = new Notification(`${event.newChat ? "New chat" : "Customer reply"} · ${siteName}`, { body: event.preview, tag: key });
        notification.onclick = () => {
          window.focus(); onSelectSite(siteId); setSelected({ siteId, conversationId: event.conversationId }); notification.close();
        };
      }
    }
  }, [alertsEnabled, onSelectSite]);

  const connectionChanged = useCallback((siteId: string, state: string) => setConnections((current) => ({ ...current, [siteId]: state })), []);
  const refreshTranscript = useCallback(async (target: { siteId: string; conversationId: string }) => {
    const result = await api<Transcript>(`/api/admin/sites/${target.siteId}/live/${target.conversationId}`);
    if (selectedRef.current?.siteId === target.siteId && selectedRef.current.conversationId === target.conversationId) setTranscript(result);
  }, [api]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void refreshTranscript(selected).catch((error: unknown) => {
        if (!cancelled) setProblem(error instanceof Error ? error.message : "Could not load conversation");
      });
    }, 100);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [selected, revision, refreshTranscript]);

  useEffect(() => { bottom.current?.scrollIntoView({ block: "nearest" }); }, [transcript]);
  useEffect(() => {
    if (selected && selected.siteId !== selectedSiteId) { setSelected(null); setTranscript(null); setDraft(""); }
  }, [selected, selectedSiteId]);
  useEffect(() => () => { void audio.current?.close(); }, []);

  function openChat(siteId: string, conversationId: string) {
    onSelectSite(siteId);
    setSelected({ siteId, conversationId });
    setTranscript(null); setDraft(""); setProblem(""); pendingMessage.current = null;
    setAlerts((current) => current.filter((item) => item.siteId !== siteId || item.conversationId !== conversationId));
  }

  async function enableAlerts() {
    if (alertsEnabled) { setAlertsEnabled(false); setAlertStatus("Sound and desktop alerts are off. In-app alerts remain on."); return; }
    try {
      audio.current ??= new AudioContext();
      await audio.current.resume();
      const permission = "Notification" in window ? await Notification.requestPermission() : "unsupported";
      setAlertsEnabled(true);
      setAlertStatus(permission === "granted" ? "Sound and desktop alerts are on while this admin is open." : "Sound and in-app alerts are on. Desktop notifications are unavailable or blocked in this browser.");
    } catch { setAlertStatus("In-app alerts are on. This browser could not enable sound or desktop alerts."); }
  }

  async function act(action: "takeover" | "release" | "message", event?: FormEvent) {
    event?.preventDefault();
    if (!selected || busy) return;
    const target = selected;
    setBusy(true); setProblem("");
    const text = draft.trim();
    if (action === "message" && (!pendingMessage.current || pendingMessage.current.text !== text || pendingMessage.current.conversationId !== target.conversationId)) {
      pendingMessage.current = { id: crypto.randomUUID(), text, conversationId: target.conversationId };
    }
    try {
      const result = await api<Transcript>(`/api/admin/sites/${target.siteId}/live/${target.conversationId}`, {
        method: "POST", body: JSON.stringify({ action, operatorId, name, ...(action === "message" ? { text, messageId: pendingMessage.current!.id } : {}) }),
      });
      if (selectedRef.current?.conversationId === target.conversationId && selectedRef.current.siteId === target.siteId) {
        setTranscript(result);
        if (action === "message") { setDraft(""); pendingMessage.current = null; }
      }
    } catch (error) { setProblem(error instanceof Error ? error.message : "Unable to update chat"); }
    finally { setBusy(false); }
  }

  const visitors = snapshots[selectedSiteId]?.visitors ?? [];
  const site = sites.find((item) => item.id === selectedSiteId);
  const currentVisitor = selected ? snapshots[selected.siteId]?.visitors.find((visitor) => visitor.conversationId === selected.conversationId) : null;
  const owner = currentVisitor ? currentVisitor.support : transcript?.support;
  const connected = Boolean(selected && connections[selected.siteId] === "Connected");
  const canReply = connected && !busy;

  return <section className="panel live-inbox" aria-label="Live customer support">
    {sites.filter((item) => item.status === "active").map((item) => <SiteLiveConnection key={item.id} siteId={item.id} siteName={item.name} operatorId={operatorId} name={name} api={api} receive={receive} connectionChanged={connectionChanged} />)}
    <div className="live-inbox-heading">
      <div><p className="eyebrow">Customer support</p><h2>Live inbox <span className="live-count">{visitors.filter((visitor) => visitor.online).length} online</span></h2><p>All connected admins can help in the same chat. Reply within {formatTakeoverDelay(resolveTakeoverDelay(site?.aiTakeoverDelaySeconds))} or AI will step in.</p></div>
      <button type="button" className="button button-secondary" onClick={() => void enableAlerts()}>{alertsEnabled ? "Mute alerts" : "Enable sound & desktop alerts"}</button>
    </div>
    <div className="live-inbox-toolbar">
      <form onSubmit={(event) => { event.preventDefault(); if (nameDraft.trim()) { setName(nameDraft.trim()); sessionStorage.setItem(nameKey, nameDraft.trim()); } }}>
        <label htmlFor="support-name">Your name for customers</label><input id="support-name" value={nameDraft} maxLength={60} onChange={(event) => setNameDraft(event.target.value)} required /><button type="submit" className="text-button" disabled={name === nameDraft.trim()}>Save name</button>
      </form>
      <span role="status">{site?.status === "active" ? connections[selectedSiteId] ?? "Connecting…" : "Select an active website"} · {site?.name}</span>
    </div>
    <ProfilePhoto api={api} name={name} />
    {alertStatus && <p className="live-alert-help">{alertStatus}</p>}
    <div className="live-alerts" aria-live="polite">
      {alerts.map((alert) => <button key={`${alert.siteId}:${alert.eventId}`} onClick={() => openChat(alert.siteId, alert.conversationId)}><strong>{alert.newChat ? "New chat" : "Customer reply"} · {alert.siteName}</strong><span>{alert.preview}</span><span>Open chat →</span></button>)}
    </div>
    <div className="live-inbox-grid">
      <div className="live-visitor-list" aria-label="Visitors and recent conversations">
        <h3>Visitors & recent chats</h3>
        {visitors.length === 0 && <p className="live-empty">Visitors will appear here as they browse your website.</p>}
        {visitors.map((visitor) => <VisitorRow key={visitor.conversationId} visitor={visitor} selected={selected?.siteId === selectedSiteId && selected.conversationId === visitor.conversationId} onClick={() => openChat(selectedSiteId, visitor.conversationId)} />)}
      </div>
      <div className="live-conversation">
        {!selected ? <div className="live-empty"><h3>Be there when a shopper needs you</h3><p>Select a visitor to see their conversation and offer help.</p><p>Alerts cover all active websites while this admin page stays open.</p></div> : <>
          <div className="live-thread-heading"><div><h3>Visitor {selected.conversationId.slice(0, 6)}</h3><span>{currentVisitor?.online ? currentVisitor.visible ? "Browsing now" : "Tab in background" : "Offline · messages are saved"} · {owner ? `Shared team chat · ${owner.name} is helping` : "AI is answering"}</span></div><div className="live-thread-actions">
            {owner ? <button className="button button-secondary" disabled={!connected || busy} onClick={() => void act("release")}>Return to AI</button> : <button className="button button-primary" disabled={!canReply} onClick={() => void act("takeover")}>Take over</button>}
          </div></div>
          {owner?.replyDueAt && <ReplyCountdown dueAt={owner.replyDueAt} />}
          {currentVisitor?.pageUrl && <a className="live-current-page" href={currentVisitor.pageUrl} target="_blank" rel="noreferrer">Current page: {currentVisitor.pageTitle || currentVisitor.pageUrl}<small>{currentVisitor.pageUrl}</small></a>}
          <div className="live-transcript" aria-live="polite">
            {!transcript ? <p className="live-empty">Loading conversation…</p> : transcript.messages.length === 0 ? <p className="live-empty">This visitor hasn’t started a chat yet. Send a helpful hello; they’ll see a quiet reply indicator without the chat opening automatically.</p> : transcript.messages.map((message) => <TranscriptMessage key={message.id} message={message} />)}
            <div ref={bottom} />
          </div>
          {problem && <p role="alert" className="chat-insights-error">{problem}</p>}
          <form className="live-reply" onSubmit={(event) => void act("message", event)}>
            <label htmlFor="live-reply">Message from {name}</label><textarea id="live-reply" rows={3} maxLength={2_000} value={draft} onChange={(event) => setDraft(event.target.value)} disabled={!canReply} placeholder="How can I help you today?" />
            <div><small>{owner ? "Anyone on the connected team can reply. Each message shows its sender’s name." : "Sending a message starts shared team support and pauses the AI."}</small><button className="button button-primary" type="submit" disabled={!canReply || !draft.trim()}>{busy ? "Sending…" : "Send message"}</button></div>
          </form>
        </>}
      </div>
    </div>
    <p className="live-alert-help">No reply before the countdown ends, or all admins disconnected? AI takes over. Any connected admin can reply or return the shared chat to AI. Change the AI takeover delay in this website’s settings below. After a timeout or “Return to AI”, select “Take over” to resume helping. Recent conversations are retained for 60 days.</p>
  </section>;
}

function SiteLiveConnection({ siteId, siteName, operatorId, name, api, receive, connectionChanged }: {
  siteId: string; siteName: string; operatorId: string; name: string; api: Api;
  receive: (siteId: string, siteName: string, event: HubEvent) => void;
  connectionChanged: (siteId: string, state: string) => void;
}) {
  const handler = useRef(receive);
  handler.current = receive;
  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | undefined;
    let timer: number | undefined;
    let heartbeat: number | undefined;
    let retry = 1_000;
    async function connect() {
      connectionChanged(siteId, "Connecting…");
      try {
        const { token } = await api<{ token: string }>(`/api/admin/sites/${siteId}/live/session`, { method: "POST", body: JSON.stringify({ operatorId, name }) });
        if (disposed) return;
        const url = new URL("/api/support/socket", window.location.origin);
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:"; url.searchParams.set("token", token);
        socket = new WebSocket(url);
        const ping = () => { if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "presence" })); };
        socket.onopen = () => { retry = 1_000; connectionChanged(siteId, "Connected"); ping(); heartbeat = window.setInterval(ping, 20_000); };
        socket.onmessage = (event) => { try {
          const message = JSON.parse(event.data);
          if (message.type === "signed-out") { socket?.close(1000, "Signed out"); return; }
          handler.current(siteId, siteName, message as HubEvent);
        } catch { /* Ignore malformed events. */ } };
        socket.onclose = reconnect;
        socket.onerror = () => socket?.close();
      } catch { reconnect(); }
    }
    function reconnect() {
      window.clearInterval(heartbeat);
      if (disposed) return;
      connectionChanged(siteId, "Reconnecting…");
      timer = window.setTimeout(() => void connect(), retry);
      retry = Math.min(retry * 2, 30_000);
    }
    void connect();
    return () => { disposed = true; window.clearTimeout(timer); window.clearInterval(heartbeat); socket?.close(1000, "Admin left"); };
  }, [api, connectionChanged, name, operatorId, siteId, siteName]);
  return null;
}

function VisitorRow({ visitor, selected, onClick }: { visitor: LiveVisitor; selected: boolean; onClick: () => void }) {
  return <button className={`live-visitor ${selected ? "selected" : ""}`} onClick={onClick}>
    <span><i className={visitor.online ? "online" : "offline"} /><strong>Visitor {visitor.conversationId.slice(0, 6)}</strong><small>{visitor.support ? "Human" : "AI"}</small></span>
    <span className="live-visitor-page">{visitor.online ? visitor.pageTitle || visitor.pageUrl || "Browsing the store" : "Offline"}</span>
    <span className="live-visitor-preview">{visitor.preview || "Hasn’t started chatting yet"}</span>
    {visitor.support?.replyDueAt && <ReplyCountdown dueAt={visitor.support.replyDueAt} />}
  </button>;
}

function ReplyCountdown({ dueAt }: { dueAt: number }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [dueAt]);
  const seconds = Math.max(0, Math.ceil((dueAt - now) / 1_000));
  return <span className="live-reply-countdown">{seconds ? `Reply within ${seconds}s · then AI steps in` : "Handing over to AI…"}</span>;
}

function TranscriptMessage({ message }: { message: UIMessage }) {
  const metadata = message.metadata as { humanAdmin?: string } | undefined;
  const text = message.parts.flatMap((part) => part.type === "text" ? [part.text] : []).join("\n");
  const products = new Map<string, string>();
  const categories: string[] = [];
  for (const part of message.parts) {
    if (!("output" in part) || !part.output || typeof part.output !== "object") continue;
    const output = part.output as { products?: unknown[]; categories?: unknown[] };
    if (Array.isArray(output.products)) for (const value of output.products) {
      if (!value || typeof value !== "object" || !("name" in value) || !("permalink" in value)) continue;
      if (typeof value.name === "string" && typeof value.permalink === "string" && /^https?:\/\//.test(value.permalink)) products.set(value.permalink, value.name);
    }
    if (Array.isArray(output.categories)) for (const value of output.categories) {
      if (value && typeof value === "object" && "name" in value && typeof value.name === "string") categories.push(value.name);
    }
  }
  if (!text && !products.size && !categories.length) return null;
  return <div className={`live-message ${message.role === "user" ? "customer" : "staff"}`}>
    <small>{message.role === "user" ? "Customer" : metadata?.humanAdmin ? `${metadata.humanAdmin} · Store team` : "AI assistant"}</small>
    {text && <p>{decodeHtmlEntities(text)}</p>}
    {categories.length > 0 && <p>Suggested categories: {categories.map(decodeHtmlEntities).join(", ")}</p>}
    {products.size > 0 && <div className="live-recommendations">{[...products].map(([url, name]) => <a href={url} target="_blank" rel="noreferrer" key={url}>{decodeHtmlEntities(name)} ↗</a>)}</div>}
  </div>;
}
